import React from "react";
import { MapPin, Camera } from "lucide-react";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";

/**
 * Renders the per-pool Before/After photo gallery for a closed invoice.
 * Also shows optional proof-of-delivery photos uploaded by the driver on
 * products-only tickets (no AI validation, just a paper trail).
 * Hidden when the invoice has neither.
 */
export default function DeliveryPhotos({ photos, extraPhotos }) {
  const list = (photos || []).filter((p) => p.photo_before_data_url || p.photo_after_data_url);
  const extras = (extraPhotos || []).filter(Boolean);
  if (list.length === 0 && extras.length === 0) return null;
  return (
    <div className="mt-8 pt-6 border-t border-primary/30">
      <div className="flex items-center gap-2 mb-4">
        <Camera className="w-4 h-4 text-primary" />
        <h3 className="overline text-primary">Delivery photos</h3>
      </div>
      <div className="space-y-5">
        {list.map((p, i) => (
          <div key={p.pool_id || `dp-${i}-${p.pool_name || ""}`} className="border border-primary/20 bg-background rounded-sm p-4" data-testid={`invoice-photos-${i}`}>
            {p.pool_name && (
              <div className="mb-3">
                <div className="font-semibold text-sm">{p.pool_name}</div>
                {p.pool_address && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {p.pool_address}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <PhotoCell src={p.photo_before_data_url} label="Before · filling tank" testId={`invoice-photo-before-${i}`} />
              <PhotoCell src={p.photo_after_data_url} label="After · finished" testId={`invoice-photo-after-${i}`} />
            </div>
          </div>
        ))}
        {extras.length > 0 && (
          <div className="border border-primary/20 bg-background rounded-sm p-4" data-testid="invoice-extra-photos">
            <div className="font-semibold text-sm mb-3">Proof of delivery</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {extras.map((src, i) => (
                <PhotoCell key={`extra-${i}-${src.slice(-16)}`} src={src} label={`Photo ${i + 1}`} testId={`invoice-extra-photo-${i}`} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PhotoCell({ src, label, testId }) {
  return (
    <div>
      {src ? (
        <LivePhotoSurface value={src} alt={label} className="w-full aspect-square rounded-sm border border-border" testId={testId} eagerMotion />
      ) : (
        <div className="w-full aspect-square flex items-center justify-center bg-secondary/40 border border-dashed border-border text-xs text-muted-foreground">
          Not provided
        </div>
      )}
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
    </div>
  );
}
